<script lang="ts">
  import { browser } from '$app/environment';
  import { invalidate } from '$app/navigation';
  import LoadingState from '$components/LoadingState.svelte';
  import PreferencesModal from '$components/PreferencesModal.svelte';
  import PriceAlertsModal from '$components/PriceAlertsModal.svelte';
  import { filterQuery } from '$components/QueryFilterInput.svelte';
  import UnsubscribedUserBanner from '$components/UnsubscribedUserBanner.svelte';
  import PriceGrid, {
    runnerMarketMapping
  } from '$components/race/PriceGrid.svelte';
  import RaceError from '$components/race/RaceError.svelte';
  import RaceHeader from '$components/race/RaceHeader.svelte';
  import RaceInfoBar from '$components/race/RaceInfoBar.svelte';
  import { loadingRaces } from '$components/race/RaceList.svelte';
  import RaceAlertToast, {
    type RaceAlertToastProps
  } from '$components/toasts/RaceAlertToast.svelte';
  import type {
    BookmakerMarket,
    EventAlertsResponse,
    GQLError,
    MarketType,
    RaceBetfairUpdateResponse,
    RacePriceUpdateResponse,
    RaceUpdateResponse,
    RunnerTopPrices
  } from '$types/races.js';
  import { isSubscribed } from '$utils/auth';
  import {
    Bookmaker,
    bestPriceBookmakerPreferences,
    feedBookmakers
  } from '$utils/bookmakers.js';
  import { isFiltering, raceFilters } from '$utils/filters.js';
  import { getRaceLink, gotoRace, selectedRaceId } from '$utils/navigation.js';
  import {
    MarketMap,
    bookmakerOverroundStore,
    filterRace,
    isRaceClosed,
    isVacantBox,
    selectedRaceHasSP
  } from '$utils/race.js';
  import { activeSiteConfig } from '$utils/ui.js';
  import { onDestroy, onMount } from 'svelte';
  import toast, { Toaster } from 'svelte-french-toast';
  import { get, writable, type Writable } from 'svelte/store';

  export let data;
  $: ({
    title,
    race,
    races,
    streamed,
    user,
    eventAlerts,
    session,
    bookmakersWithFlucs,
    layoutConfigStore: { activeLayoutConfig },
    subscriptionClient,
    dateFrom,
    dateTo,
    hasCompanion
  } = data);

  // make sure the loading spinner doesn't show when loading the page directly
  $: {
    if (!$selectedRaceId && $race?.id) selectedRaceId.set($race?.id);
  }
  $: {
    if ($race?.id === $selectedRaceId) {
      loadingRaces.set(false);
    }
  }

  $: lastRacesRefreshedTime = Date.now();

  // we are loading if the current raceId does not match the data
  // OR if there is an error returned, then we aren't loading
  $: isRacesError = !(($races?.errors?.length ?? 0) === 0);
  $: isRaceDataLoading =
    $race?.id !== $selectedRaceId ||
    (streamed?.raceData?.data?.race.id !== $selectedRaceId && !isRaceDataError);
  $: isRaceDataError = (streamed?.raceData?.errors?.length ?? 0) > 0;

  // log any errors
  $: {
    if (streamed?.raceData?.errors?.length) {
      console.error('could not parse race data', streamed?.raceData.errors);
    }
    if ($races.errors?.length) {
      console.error('could not load races', $races.errors);
    }
  }
  $: filteredRaces =
    $races.data?.races.filter((r) => {
      if (filterRace($filterQuery, $raceFilters, r, $isFiltering)) {
        return true;
      }
      return false;
    }) ?? [];

  // calculate the market % for each bookmaker
  $: marketOverrounds = new Map<
    Bookmaker,
    {
      fixedWin: ReturnType<typeof bookmakerOverroundStore>;
      fixedPlace: ReturnType<typeof bookmakerOverroundStore>;
    }
  >();

  // create a map of market ids to market stores
  $: marketMapping = new MarketMap();
  $: runnerTopPricesMap = new Map<
    string,
    {
      fixedWin: Writable<BookmakerMarket[]>;
      fixedPlace: Writable<BookmakerMarket[]>;
    }
  >();

  $: bestPriceOverrounds = {
    fixedWin: 0,
    fixedPlace: 0
  };
  function getTopPricesOverrounds() {
    let fixedWinTotal = 0;
    let fixedPlaceTotal = 0;
    for (const [runnerId, marketOverround] of runnerTopPricesMap.entries()) {
      const runner = $race?.runners.find((r) => r.id === runnerId);
      if (!runner || runner?.scratchedTime) {
        continue;
      }

      const fixedWin = get(marketOverround.fixedWin)?.[0];
      const fixedPlace = get(marketOverround.fixedPlace)?.[0];

      if (fixedWin.fixedWin.price) {
        fixedWinTotal += 1 / fixedWin.fixedWin.price;
      }
      if (fixedWin.fixedWin.price) {
        fixedPlaceTotal += 1 / fixedPlace.fixedPlace.price;
      }
    }

    if (!bestPriceOverrounds) {
      bestPriceOverrounds = {
        fixedWin: 0,
        fixedPlace: 0
      };
    }
    bestPriceOverrounds.fixedWin = fixedWinTotal * 100;
    bestPriceOverrounds.fixedPlace = fixedPlaceTotal * 100;

    return bestPriceOverrounds;
  }

  // update the top 3 prices for each runner
  function updateRunnerTopPrices(
    runnerTopPrices: RunnerTopPrices,
    market: BookmakerMarket,
    { updateSamePrice = false } = {}
  ) {
    // get non-scratched runners
    // const activeRunners = streamed?.raceData?.data?.race?.runners.filter(
    //   (r) => !r.scratchedTime && !isVacantBox(r)
    // );

    // create const of 'fixedWin' and 'fixedPlace' to iterate over
    // use the MarketType type to ensure we are only using the correct keys
    const marketTypes: MarketType[] = ['fixedWin', 'fixedPlace'];
    marketTypes.forEach((marketType) => {
      runnerTopPrices[marketType].update((rtp) => {
        if (!rtp) return [market];

        // if the bookmaker is already in the top 3 but the price has changed, update it
        // this may mean that the bookmaker is no longer in the top 3, but it will get sorted to last
        // and then pushed out of the array
        const existingIndexWithDifferentPrice = rtp.findIndex(
          (rtp) =>
            rtp.bookmaker === market.bookmaker &&
            rtp[marketType].price !== market[marketType].price
        );

        if (existingIndexWithDifferentPrice !== -1) {
          rtp[existingIndexWithDifferentPrice] = market;
          rtp.sort((a, b) => {
            const diff = b[marketType].price - a[marketType].price;
            if (diff !== 0) return diff;

            return (
              a[marketType].lastUpdated.getTime() -
              b[marketType].lastUpdated.getTime()
            );
          });
          // only keep the top 3
          rtp.splice(3);
        } else if (
          rtp[rtp.length - 1][marketType].price <= market[marketType].price &&
          !rtp.find((rtp) => rtp[marketType].price === market[marketType].price)
        ) {
          rtp.push(market);
          rtp.sort((a, b) => {
            const diff = b[marketType].price - a[marketType].price;
            if (diff !== 0) return diff;

            return (
              a[marketType].lastUpdated.getTime() -
              b[marketType].lastUpdated.getTime()
            );
          });
          // only keep the top 3
          // rtp.splice(3);
        } else if (
          bestPriceBookmakerPreferences.includes(market.bookmaker as Bookmaker)
        ) {
          // if the market is a best price bookmaker, remove the last market
          const existingIndex = rtp.findIndex(
            (rtp) => rtp[marketType].price === market[marketType].price
          );
          // if the price exists, replace it if it isn't a preferred bookmaker
          if (existingIndex !== -1) {
            // if the existing isn't a best price bookmaker, replace it with the new one
            const existingIsPreferred = bestPriceBookmakerPreferences.includes(
              rtp[existingIndex].bookmaker as Bookmaker
            );
            if (!existingIsPreferred) {
              rtp[existingIndex] = market;
            } else if (existingIsPreferred) {
              // random chance of replacing the existing market
              if (Math.random() > 0.7) {
                rtp[existingIndex] = market;
              }
            }
          }
        } else if (updateSamePrice) {
          // we can update the same price if the bookmaker is not a best price bookmaker
          // this is so we can get some randomisation in the prices
          // this will happen on load and not on updates
          const existingIndex = rtp.findIndex(
            (rtp) => rtp[marketType].price === market[marketType].price
          );
          if (existingIndex !== -1) {
            // 30% chance of replacing the existing market
            if (Math.random() > 0.5) {
              rtp[existingIndex] = market;
            }
          }
        }
        return rtp;
      });
    });
  }

  let sortRunners: (() => void) | undefined = undefined;
  $: {
    if (streamed?.raceData && streamed?.raceData?.data?.race.id) {
      marketOverrounds.clear();
      marketMapping.clear();
      runnerMarketMapping.clear();
      selectedRaceHasSP.set(false);
      // only populate once, on race load

      if (streamed.raceData.data?.race)
        streamed.raceData.data.race.startTime = new Date(
          streamed.raceData.data.race.startTime
        );

      streamed.raceData.data?.race?.links?.forEach((link) => {
        // parse to date
        if (link.lastSuccessfulPriceUpdate)
          link.lastSuccessfulPriceUpdate = new Date(
            link.lastSuccessfulPriceUpdate
          );
      });

      streamed?.raceData.data?.race.runners.forEach((runner) => {
        if (runner.scratchedTime) {
          runner.scratchedTime = new Date(runner.scratchedTime);
        }

        runner.betfairMarkets?.forEach((market) => {
          // add runner to market mapping
          runnerMarketMapping.set(market.id, runner.id);

          // parse date
          market.back.forEach((m) => {
            m.lastUpdated = new Date(m.lastUpdated);
          });
          market.lay.forEach((m) => {
            m.lastUpdated = new Date(m.lastUpdated);
          });
          market?.backFlucs?.forEach((m) => {
            m.lastUpdated = new Date(m.lastUpdated);
          });
          market?.layFlucs?.forEach((m) => {
            m.lastUpdated = new Date(m.lastUpdated);
          });

          if (!$selectedRaceHasSP && market.sp !== null && market.sp > 0) {
            selectedRaceHasSP.set(true);

            // also update the race status if open
            if ($race?.status === 'Open')
              races.update(($races) => {
                const matchedIndex = $races.data?.races.findIndex(
                  (r) => r.id === $selectedRaceId
                );
                if (
                  matchedIndex &&
                  matchedIndex !== -1 &&
                  $races.data?.races?.[matchedIndex]
                ) {
                  $races.data.races[matchedIndex].status = 'Closed';
                }
                return $races;
              });
          }

          marketMapping.set(market.id, writable(market));
        });
        runner.bookmakerMarkets?.forEach((market) => {
          // add runner to market mapping
          runnerMarketMapping.set(market.id, runner.id);

          // parse date
          market.fixedWin.lastUpdated = new Date(market.fixedWin.lastUpdated);
          market.fixedPlace.lastUpdated = new Date(
            market.fixedPlace.lastUpdated
          );

          market.fixedWin.flucs?.forEach((f) => {
            f.lastUpdated = new Date(f.lastUpdated);
          });
          market.fixedPlace.flucs?.forEach((f) => {
            f.lastUpdated = new Date(f.lastUpdated);
          });

          marketMapping.set(market.id, writable(market));

          //   update market overrounds
          if (!runner.scratchedTime && !isVacantBox(runner)) {
            if (!marketOverrounds.has(market.bookmaker as Bookmaker)) {
              marketOverrounds.set(market.bookmaker as Bookmaker, {
                fixedWin: bookmakerOverroundStore(market, 'fixedWin'),
                fixedPlace: bookmakerOverroundStore(market, 'fixedPlace')
              });
            } else {
              const bookmakerOverrounds = marketOverrounds.get(
                market.bookmaker as Bookmaker
              );
              if (bookmakerOverrounds) {
                bookmakerOverrounds.fixedWin.addMarket(market, 'fixedWin');
                bookmakerOverrounds.fixedPlace.addMarket(market, 'fixedPlace');
              }
            }
          }
        });

        // only update top prices once all markets have been loaded
        runner.bookmakerMarkets?.forEach((market) => {
          // skip if the market is old
          const marketLastUpdated = streamed?.raceData?.data?.race.links.find(
            (l) => l.bookmaker === market.bookmaker
          )?.lastSuccessfulPriceUpdate;
          if (
            streamed?.raceData?.data?.race.status === 'Open' &&
            marketLastUpdated &&
            marketLastUpdated.getTime() <
              new Date().getTime() - 1000 * 60 * 60 * 3 &&
            !feedBookmakers.includes(market.bookmaker)
          ) {
            console.log('skipping old market', market.bookmaker);
            return;
          }

          const runnerTopPrices = runnerTopPricesMap.get(runner.id);
          if (!runnerTopPrices) {
            runnerTopPricesMap.set(runner.id, {
              fixedWin: writable([market]),
              fixedPlace: writable([market])
            });
          } else {
            updateRunnerTopPrices(runnerTopPrices, market, {
              updateSamePrice: true
            });
          }
        });
      });
      getTopPricesOverrounds();
    }
  }
  // race specific subscriptions
  let raceUnsubscribers: ((() => void) | undefined)[] = [];
  let raceIdLastSubscribedTo = ''; // avoid resubscribing when we just refresh the data
  let isRefreshing = false; // flag to refresh the race when we receive a new market
  $: {
    if ($race && raceIdLastSubscribedTo !== $race?.id) {
      raceUnsubscribers.forEach((u) => u?.());
      raceUnsubscribers = [];
      if ($race && $isSubscribed && subscriptionClient) {
        raceIdLastSubscribedTo = $race.id;
        raceUnsubscribers.push(
          subscriptionClient.subscribeRacePriceUpdates($race, {
            next: (data: RacePriceUpdateResponse) => {
              // only sort if the price updates are for our sort bookmaker
              let needsSorting = false;

              // update the marketMapping
              data?.data?.priceUpdates.forEach((pu) => {
                if (
                  ($activeLayoutConfig.runnerSort === 'PRICE_ASC' ||
                    $activeLayoutConfig.runnerSort === 'PRICE_DESC') &&
                  pu.bookmaker === $activeLayoutConfig.runnerPriceSortBookmaker
                ) {
                  needsSorting = true;
                }

                // parse date
                pu.fixedWin.lastUpdated = new Date(pu.fixedWin.lastUpdated);
                pu.fixedPlace.lastUpdated = new Date(pu.fixedPlace.lastUpdated);

                //   update market overrounds
                if (!marketOverrounds.has(pu.bookmaker as Bookmaker)) {
                  marketOverrounds.set(pu.bookmaker as Bookmaker, {
                    fixedWin: bookmakerOverroundStore(pu, 'fixedWin'),
                    fixedPlace: bookmakerOverroundStore(pu, 'fixedPlace')
                  });
                } else {
                  const bookmakerOverrounds = marketOverrounds.get(
                    pu.bookmaker as Bookmaker
                  );
                  if (bookmakerOverrounds) {
                    bookmakerOverrounds.fixedWin.addMarket(pu, 'fixedWin');
                    bookmakerOverrounds.fixedPlace.addMarket(pu, 'fixedPlace');
                  }
                }

                if (marketMapping.has(pu.id)) {
                  // check if the market has flucs, and add the update if so
                  marketMapping.getBookmakerMarket(pu.id)?.update((market) => {
                    // TODO add flucs to market
                    const bm = pu as BookmakerMarket;
                    // add previous price for change classes
                    // bm.fixedWin.previousPrice = market.fixedWin.price;
                    // bm.fixedPlace.previousPrice = market.fixedPlace.price;

                    // bm.fixedWin.flucs = market.fixedWin.flucs;
                    // bm.fixedPlace.flucs = market.fixedPlace.flucs;

                    market.fixedWin.previousPrice = market.fixedWin.price;
                    market.fixedPlace.previousPrice = market.fixedPlace.price;

                    if (
                      (!market.fixedPlace.price && bm.fixedPlace.price) ||
                      (!market.fixedWin.price && bm.fixedWin.price)
                    ) {
                      // we we are updating a zero price with a non-zero price
                      // the market is probably new so we will force a refresh to rerender
                      //   NOTE: i can probably do this without a reload...
                      if (!isRefreshing) {
                        isRefreshing = true;
                        setTimeout(async () => {
                          await invalidate('betwatch:race');
                          isRefreshing = false;
                          sortRunners?.();
                        }, 2000);
                      }
                    }

                    market.fixedWin.price = bm.fixedWin.price;
                    market.fixedWin.lastUpdated = bm.fixedWin.lastUpdated;
                    market.fixedPlace.price = bm.fixedPlace.price;
                    market.fixedPlace.lastUpdated = bm.fixedPlace.lastUpdated;

                    // also push to bookmaker flucs array if included
                    if (bookmakersWithFlucs?.includes(market.bookmaker)) {
                      if (!market.fixedWin.flucs) market.fixedWin.flucs = [];
                      market.fixedWin.flucs.push({
                        price: market.fixedWin.price,
                        lastUpdated: market.fixedWin.lastUpdated
                      });
                      if (!market.fixedPlace.flucs)
                        market.fixedPlace.flucs = [];
                      market.fixedPlace.flucs?.push({
                        price: market.fixedPlace.price,
                        lastUpdated: market.fixedPlace.lastUpdated
                      });
                    }

                    const runnerId = runnerMarketMapping.get(pu.id);
                    if (runnerId) {
                      const runnerTopPrices = runnerTopPricesMap.get(runnerId);
                      if (runnerTopPrices) {
                        updateRunnerTopPrices(runnerTopPrices, market);
                      } else {
                        // console.log(
                        //   'no runner top prices found for market',
                        //   market
                        // );
                      }
                    }

                    return market;
                  });
                } else {
                  //   console.log('adding new market', { pu });
                  marketMapping.set(pu.id, writable(pu as BookmakerMarket));
                  if (!isRefreshing) {
                    isRefreshing = true;
                    setTimeout(async () => {
                      await invalidate('betwatch:race');
                      isRefreshing = false;
                      sortRunners?.();
                    }, 2000);
                  }
                }
              });
              // apply any state changes
              if (needsSorting) sortRunners?.();

              // sortRunners?.();

              getTopPricesOverrounds();
            },
            error: (error: GQLError) => {
              console.error(error);
              throw error;
            },
            complete: () => {
              return;
            }
          })
        );
        raceUnsubscribers.push(
          subscriptionClient.subscribeRaceBetfairUpdates($race, {
            next: (data: RaceBetfairUpdateResponse) => {
              // update the marketMapping
              data?.data?.betfairUpdates.forEach((bu) => {
                // parse date
                bu.back.forEach((m) => {
                  m.lastUpdated = new Date(m.lastUpdated);
                });
                bu.lay.forEach((m) => {
                  m.lastUpdated = new Date(m.lastUpdated);
                });
                bu?.lay?.forEach((m) => {
                  m.lastUpdated = new Date(m.lastUpdated);
                });

                if (!$selectedRaceHasSP && bu.sp !== null && bu.sp > 0)
                  selectedRaceHasSP.set(true);

                if (marketMapping.has(bu.id)) {
                  // check if the market has flucs, and add the update if so
                  marketMapping.getBetfairMarket(bu.id)?.update((market) => {
                    market.back = bu.back;
                    market.lay = bu.lay;
                    market.sp = bu.sp;
                    market.totalMatched = bu.totalMatched;
                    market.marketTotalMatched = bu.marketTotalMatched;

                    // dont add flucs if market has an SP (is closed)
                    if (bu.sp && bu.sp > 0) return market;

                    // also push to bookmaker flucs array if included
                    // get last fluc
                    const lastBackFluc =
                      market.backFlucs?.[market.backFlucs.length - 1];

                    if (!lastBackFluc && market.back?.[0]) {
                      // add first fluc
                      market.backFlucs = [market.back[0]];
                    }

                    // check if lastFluc is different to current price and more than 5 seconds ago
                    else if (
                      lastBackFluc &&
                      lastBackFluc.lastUpdated.getTime() < Date.now() - 5000 &&
                      market.back?.[0].price !== lastBackFluc.price
                    ) {
                      market.backFlucs?.push(market.back[0]);

                      // max length of flucs is 1000
                      // if (market?.flucs && (market.flucs?.length ?? 0) > 1000) {
                      //   market.flucs = market.flucs.slice(
                      //     market.flucs.length - 1000,
                      //     market.flucs.length
                      //   );
                      // }
                    }

                    const lastLayFluc =
                      market.layFlucs?.[market.layFlucs.length - 1];
                    if (!lastLayFluc && market.lay?.[0]) {
                      // add first fluc
                      market.layFlucs = [market.lay[0]];
                    } else if (
                      lastLayFluc &&
                      lastLayFluc.lastUpdated.getTime() < Date.now() - 5000 &&
                      market.lay?.[0].price !== lastLayFluc.price
                    ) {
                      market.layFlucs?.push(market.lay[0]);
                    }

                    return market;
                  });
                } else {
                  marketMapping.set(bu.id, writable(bu));
                }
              });

              // apply any state changes
              if (
                ($activeLayoutConfig.runnerSort === 'PRICE_ASC' ||
                  $activeLayoutConfig.runnerSort === 'PRICE_DESC') &&
                'Betfair' === $activeLayoutConfig.runnerPriceSortBookmaker
              ) {
                sortRunners?.();
              }
            },
            error: (error: GQLError) => {
              console.error(error);
              throw error;
            },
            complete: () => {
              return;
            }
          })
        );
      }
    }
  }

  // $: title = $race?.id
  //   ? `${
  //       $isSubscribed
  //         ? `R${$race.number} ${$race.meeting?.track} - Betwatch`
  //         : `R${$race.number} ${
  //             $race.meeting?.track
  //           } - ${$race.startTime?.toLocaleDateString('en-AU', {
  //             day: 'numeric',
  //             month: 'short',
  //             year: 'numeric'
  //           })} - Betwatch Racing Odds Comparison`
  //     }`
  //   : 'Betwatch - Live Racing Odds Comparison';

  // check if betwatch companion is installed
  $: (!$hasCompanion || $hasCompanion) &&
    browser &&
    setTimeout(() => {
      const betwatchCompanion = document.querySelector('#betwatch-companion');
      if (betwatchCompanion) {
        hasCompanion.set(true);
      } else {
        hasCompanion.set(false);
      }
    }, 100);

  let autoNavTimeout: NodeJS.Timeout | undefined;

  onMount(() => {
    // check if betwatch companion is installed
    setTimeout(() => {
      const betwatchCompanion = document.querySelector('#betwatch-companion');
      if (betwatchCompanion) {
        hasCompanion.set(true);
      } else {
        hasCompanion.set(false);
      }
    }, 100);

    let subscribers: ((() => void) | undefined)[] = [];
    if ($isSubscribed && subscriptionClient) {
      subscribers.push(
        subscriptionClient.subscribeRaceUpdates(dateFrom, dateTo, {
          next: (d: RaceUpdateResponse) => {
            if (!d?.data) return;
            races.update(($races) => {
              if (!d.data) return $races;
              const matchedIndex = $races.data?.races.findIndex(
                (r) => r.id === d.data?.racesUpdates.id
              );
              if (
                matchedIndex &&
                matchedIndex !== -1 &&
                $races.data?.races?.[matchedIndex]
              ) {
                $races.data.races[matchedIndex].startTime = new Date(
                  d.data?.racesUpdates.startTime
                );
                $races.data.races[matchedIndex].status =
                  d.data?.racesUpdates.status;
                $races.data.races[matchedIndex].results =
                  d.data?.racesUpdates.results;
              }
              return $races;
            });

            // if the race update was for the current race
            if (d.data?.racesUpdates.id === $selectedRaceId && $race) {
              if (isRaceClosed($race) && $activeSiteConfig.autoEventNav) {
                const nextRace =
                  filteredRaces?.[filteredRaces.indexOf($race) + 1];
                if (nextRace) {
                  autoNavTimeout = setTimeout(() => {
                    gotoRace(nextRace);
                  }, 10000); // Wait for 10 seconds before going to next race
                }
              }
            }
          },
          error: (error: GQLError) => {
            console.error(error);
            throw error;
          },
          complete: () => {
            return;
          }
        })
      );
      subscribers.push(
        subscriptionClient.subscribeEventAlerts({
          next: (d: EventAlertsResponse) => {
            if (!d?.data) return;
            let icon = '🔔';
            if (d.data?.eventAlerts.trigger === 'PRICE_ALERT') icon = '🚨';
            toast(RaceAlertToast, {
              icon,
              duration: 10000,
              position: 'bottom-center',
              data: d
            } as RaceAlertToastProps);
          },
          error: (error: GQLError) => {
            console.error(error);
            throw error;
          },
          complete: () => {
            return;
          }
        })
      );
    }
    return () => {
      subscribers.forEach((u) => u?.());
    };
  });

  onDestroy(() => {
    raceUnsubscribers.forEach((u) => u?.());
    if (autoNavTimeout) clearTimeout(autoNavTimeout);
  });

  // match meeting alerts to their event alert
  $: {
    if ($eventAlerts?.data?.allEventAlerts)
      $races.data?.races.forEach((race) => {
        const matchedAlerts = $eventAlerts.data?.allEventAlerts.find(
          (ea) =>
            ea?.meeting?.track === race.meeting.track &&
            ea?.meeting.location === race.meeting.location &&
            ea?.meeting.type === race.meeting.type &&
            ea?.alerts &&
            ea.alerts.some((a) => a.trigger === 'EVENT_ADDED')
        );
        if (matchedAlerts) {
          const meetingAlert = matchedAlerts.alerts.find(
            (a) => a.trigger === 'EVENT_ADDED'
          );
          if (meetingAlert)
            eventAlerts.update((alerts) => {
              const existing = alerts.data?.allEventAlerts.find(
                (a) => a.eventId === race.id
              );
              if (existing) {
                //update
                const existingEventAdded = existing.alerts.find(
                  (a) => a.trigger === 'EVENT_ADDED'
                );
                if (existingEventAdded) {
                  existingEventAdded.value = meetingAlert.value;
                } else {
                  existing.alerts.push(meetingAlert);
                }
                existing.meeting = matchedAlerts.meeting;
              } else {
                alerts.data?.allEventAlerts.push({
                  eventId: race.id,
                  alerts: [meetingAlert],
                  meeting: matchedAlerts.meeting
                });
              }
              return alerts;
            });
        }
      });
  }
</script>

<svelte:head>
  <title>{title}</title>
  <meta
    name="description"
    content={`Compare the best odds for ${$race?.meeting.track} Race ${$race?.number} from all of Australia's top bookmakers. Compare, analyze, and make informed betting decisions.`}
  />
  <meta
    name="keywords"
    content="Racing Odds, Odds Comparison, Horse Racing, Greyhound Racing, Betting, Online Betting, Betwatch"
  />
  <meta property="og:title" content={title} />
  <meta property="og:type" content="website" />
  <meta
    property="og:description"
    content={`Compare the best odds for ${$race?.meeting.track} Race ${$race?.number} from all of Australia's top bookmakers. Compare, analyze, and make informed betting decisions.`}
  />
  <meta
    property="og:url"
    content={$race
      ? `https://www.betwatch.com${getRaceLink($race)}`
      : 'https://www.betwatch.com'}
  />
  <meta property="twitter:title" content={title} />
</svelte:head>

<svelte:window
  on:focus={() => {
    // check if races have been refreshed in the last 30 seconds
    if (lastRacesRefreshedTime && Date.now() - lastRacesRefreshedTime < 30000)
      return;
    invalidate('betwatch:races');
    lastRacesRefreshedTime = Date.now();
  }}
/>

{#if !isRacesError && $races}
  <main class="flex w-0 flex-1 flex-col lg:pl-72">
    {#if $race}
      <RaceHeader />
      <RaceInfoBar />

      <div
        class="relative flex flex-1 flex-col overflow-auto overscroll-x-contain pb-5"
      >
        {#if isRaceDataLoading}
          <LoadingState duration="1s" />
        {:else if isRaceDataError}
          <div class="flex h-full flex-col items-center justify-center">
            <RaceError stackTrace={streamed?.raceData?.errors} />
          </div>
        {:else}
          <!-- reload the whole grid when there is a new bookmaker/price added -->
          {#await streamed?.raceData}
            <LoadingState duration="1s" />
          {:then raceData}
            {#key (streamed?.raceData?.data?.race.id ?? '') && marketMapping.size}
              {#if raceData?.data?.race}
                <PriceGrid
                  race={raceData.data?.race}
                  {marketMapping}
                  {runnerTopPricesMap}
                  {marketOverrounds}
                  {bestPriceOverrounds}
                  bind:sortRunners
                />
              {:else}
                <div
                  class="flex min-h-screen flex-col items-center justify-center"
                >
                  <RaceError stackTrace={'Unknown Error'} />
                </div>
              {/if}
            {/key}
          {/await}
        {/if}
      </div>
    {/if}
    {#if !$isSubscribed && browser}
      <div class="flex-0 flex">
        <UnsubscribedUserBanner />
      </div>
    {/if}
  </main>
{:else if isRacesError}
  <div class="flex min-h-screen flex-col items-center justify-center">
    <RaceError stackTrace={$races.errors} />
  </div>
{:else if $races.data?.races.length && !$race}
  <!-- we have races but could not find the race in them -->
  <!-- could be because the filters are wrong -->
  <div class="flex min-h-screen flex-col items-center justify-center">
    <RaceError stackTrace={`Could not find race`} />
  </div>
{:else}
  <LoadingState />
{/if}

<!-- <SignupModal /> -->
<PreferencesModal />
<PriceAlertsModal />
<Toaster />
